import React, { useEffect, useState } from 'react'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts'
import styled, { createGlobalStyle } from 'styled-components'
import { API_URL } from '../../util'
import { SectionTitle, SectionTitleSize, SectionTitleStyle } from '../SectionTitle/SectionTitle'

// Interfaces for the dashboard data (should match backend)
interface KpiCategoryStats {
  onTarget: number;
  nearingTarget: number;
  belowTarget: number;
}

interface KpiCertificationStats {
  onTargetPercent: number;
  nearingTargetPercent: number;
  belowTargetPercent: number;
}

interface DashboardKpiData {
  overallAchievement: KpiCategoryStats;
  customerSatisfaction: KpiCategoryStats;
  certification: KpiCertificationStats;
}

const ChartContainer = styled.div`
  margin: 0;
  padding: 24px;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`

const ChartWrapper = styled.div<{ height: number; $sideBySide?: boolean }>`
  display: flex;
  justify-content: center;
  height: auto;
  min-width: 500px;
  width: 100%;
  margin: 0;
  padding: 0;
  ${({ $sideBySide }) => $sideBySide && `
    flex: 1 1 0;
    max-width: 1100px;
  `}
  @media (max-width: 1100px) {
    order: 1;
  }
`

const TableContainer = styled.div<{ $sideBySide?: boolean }>`
  margin: 16px 0 0 0;
  width: 100%;
  ${({ $sideBySide }) => $sideBySide && `
    flex: 1 1 0;
    min-width: 500px;
    max-width: 900px;
  `}
  @media (max-width: 1100px) {
    order: 2;
  }
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 1em;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  margin-bottom: 16px;
  & th, & td {
    border: none;
    padding: 14px 16px;
    text-align: left;
    vertical-align: middle;
    font-size: 1em;
    transition: background 0.2s;
  }
  & th {
    background-color: #f3f6fa;
    font-weight: bold;
    color: #1a2233;
    letter-spacing: 0.02em;
    border-bottom: 2px solid #e3e8ee;
  }
  & tr:nth-child(even) td {
    background: #f8fafc;
  }
  & tr:hover td {
    background: #eaf3fb;
  }
`

const COLORS = {
  onTarget: '#4CAF50',
  nearingTarget: '#FF9800',
  belowTarget: '#F44336',
}

const ChartTypeDropdown = styled.select`
  margin-top: 20px;
  margin-bottom: 16px;
  padding: 4px 8px;
  font-size: 1em;
  border-radius: 4px;
  border: 1px solid #ccc;
`

// Remove black border/focus from chart container
const NoOutlineWrapper = styled.div`
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
`

// Remove all focus/active outlines, borders, and strokes from recharts SVG elements and children
const NoChartOutline = createGlobalStyle`
  .recharts-wrapper:focus, .recharts-wrapper:active,
  .recharts-wrapper *:focus, .recharts-wrapper *:active, .recharts-wrapper *:target,
  .recharts-surface:focus, .recharts-surface:active,
  .recharts-responsive-container:focus, .recharts-responsive-container:active,
  svg:focus, svg:active, svg:target,
  .recharts-wrapper rect:focus, .recharts-wrapper rect:active, .recharts-wrapper rect:target,
  .recharts-wrapper g:focus, .recharts-wrapper g:active, .recharts-wrapper g:target,
  .recharts-wrapper path:focus, .recharts-wrapper path:active, .recharts-wrapper path:target,
  .recharts-wrapper circle:focus, .recharts-wrapper circle:active, .recharts-wrapper circle:target {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
    stroke: none !important;
  }
`

const SideBySideContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: center;
  width: 100%;
  @media (max-width: 1100px) {
    flex-direction: column;
    gap: 0;
  }
`

const KpiFontGlobal = createGlobalStyle`
  .recharts-wrapper, .recharts-text, .recharts-legend-item-text, .recharts-tooltip-label, .recharts-tooltip-item, .recharts-default-legend {
    font-family: 'Montserrat', sans-serif !important;
  }
  table, th, td {
    font-family: 'Montserrat', sans-serif !important;
  }
`

type KpiChartProps = {
  chartType: 'overallAchievement' | 'customerSatisfaction' | 'certification'
  title?: string
  showTable?: boolean
  height?: number
  tablePosition?: 'below' | 'left' | 'right' | 'none'
}

const chartTypeLabels = {
  overallAchievement: 'Achievement of All Targets',
  customerSatisfaction: 'Customer Satisfaction',
  certification: 'Certification',
}

export const KpiChart: React.FC<KpiChartProps> = ({ 
  chartType, 
  title, 
  showTable = false, 
  height = 420, 
  tablePosition: tablePositionProp = 'below',
}) => {
  const [data, setData] = useState<DashboardKpiData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [chartDisplayType, setChartDisplayType] = useState<'pie' | 'doughnut' | 'bar' | 'polar'>('pie')
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const tablePosition = tablePositionProp || 'below'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await fetch(`${API_URL}kpiDashboard`)
        if (!response.ok) {
          throw new Error('Failed to fetch KPI data')
        }
        const dashboardData = await response.json()
        setData(dashboardData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading) {
    return (
      <ChartContainer>
        <SectionTitle title={title || chartTypeLabels[chartType]} size={SectionTitleSize.Medium} style={SectionTitleStyle.Dark} />
        <ChartWrapper height={height}>
          <p>Loading KPI data...</p>
        </ChartWrapper>
      </ChartContainer>
    )
  }

  if (error || !data) {
    return (
      <ChartContainer>
        <SectionTitle title={title || chartTypeLabels[chartType]} size={SectionTitleSize.Medium} style={SectionTitleStyle.Dark} />
        <ChartWrapper height={height}>
          <p>Error loading KPI data: {error}</p>
        </ChartWrapper>
      </ChartContainer>
    )
  }

  // Transform the data for the selected chart type
  let chartData: { name: string; value: number; color: string }[]
  let isPercentage = false

  switch (chartType) {
    case 'overallAchievement':
      chartData = [
        { name: 'On Target', value: data.overallAchievement.onTarget, color: COLORS.onTarget },
        { name: 'Nearing Target', value: data.overallAchievement.nearingTarget, color: COLORS.nearingTarget },
        { name: 'Below Target', value: data.overallAchievement.belowTarget, color: COLORS.belowTarget },
      ]
      break
    case 'customerSatisfaction':
      chartData = [
        { name: 'On Target', value: data.customerSatisfaction.onTarget, color: COLORS.onTarget },
        { name: 'Nearing Target', value: data.customerSatisfaction.nearingTarget, color: COLORS.nearingTarget },
        { name: 'Below Target', value: data.customerSatisfaction.belowTarget, color: COLORS.belowTarget },
      ]
      break
    case 'certification':
      chartData = [
        { name: 'On Target', value: data.certification.onTargetPercent, color: COLORS.onTarget },
        { name: 'Nearing Target', value: data.certification.nearingTargetPercent, color: COLORS.nearingTarget },
        { name: 'Below Target', value: data.certification.belowTargetPercent, color: COLORS.belowTarget },
      ]
      isPercentage = true
      break
    default:
      chartData = []
  }

  // Filter out zero values for better chart display
  const filteredChartData = chartData.filter(item => item.value > 0)

  // For polar area, transform data for RadialBarChart - each item becomes a separate data point
  const polarData = filteredChartData.map(item => ({
    name: item.name,
    value: item.value,
    fill: item.color
  }));

  // Custom legend for polar area chart - defined inside component to access filteredChartData
  const PolarLegend = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 0 }}>
      {filteredChartData.map(item => (
        <span key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            backgroundColor: item.color,
            borderRadius: 3,
            marginRight: 6,
          }} />
          <span style={{ color: item.color, fontWeight: 500 }}>{item.name}</span>
        </span>
      ))}
    </div>
  );

  // Table JSX
  const tableJSX = (
    <TableContainer $sideBySide={tablePosition === 'left' || tablePosition === 'right'}>
      <StyledTable>
        <thead>
          <tr>
            <th>Category</th>
            <th>{isPercentage ? 'Percentage' : 'Count'}</th>
          </tr>
        </thead>
        <tbody>
          {chartData.map((item, index) => (
            <tr key={index}>
              <td>
                <span 
                  style={{ 
                    display: 'inline-block', 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: item.color, 
                    marginRight: '8px', 
                    borderRadius: '50%' 
                  }}
                />
                {item.name}
              </td>
              <td>
                {isPercentage ? `${item.value.toFixed(1)}%` : item.value}
              </td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </TableContainer>
  )

  // Determine chartHeight based on tablePosition
  const chartHeight = (tablePosition === 'left' || tablePosition === 'right') ? 420 : height;

  return (
    <ChartContainer>
      <KpiFontGlobal />
      <NoChartOutline />
      <SectionTitle title={title || chartTypeLabels[chartType]} size={SectionTitleSize.Medium} style={SectionTitleStyle.Dark} />
      <ChartTypeDropdown
        value={chartDisplayType}
        onChange={e => setChartDisplayType(e.target.value as any)}
        aria-label="Select chart type"
      >
        <option value="pie">Pie Chart</option>
        <option value="doughnut">Doughnut Chart</option>
        <option value="bar">Bar Chart</option>
        <option value="polar">Polar Area Chart</option>
      </ChartTypeDropdown>
      {(tablePosition === 'left' || tablePosition === 'right') ? (
        <SideBySideContainer>
          {tablePosition === 'left' && tableJSX}
          <NoOutlineWrapper>
            <ChartWrapper height={chartHeight} $sideBySide={false}>
              {filteredChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  {chartDisplayType === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(val: number, name: string) => 
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        } 
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ margin: 0, padding: 0 }}
                      />
                    </PieChart>
                  ) : chartDisplayType === 'doughnut' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        innerRadius={chartHeight / 6}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(val: number, name: string) => 
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        } 
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ margin: 0, padding: 0 }}
                      />
                    </PieChart>
                  ) : chartDisplayType === 'bar' ? (
                    <BarChart data={filteredChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} width={chartHeight} height={chartHeight}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip 
                        formatter={(val: number, name: string) => 
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        } 
                      />
                      <Legend
                        payload={filteredChartData.map(item => ({ value: item.name, type: 'square', color: item.color, id: item.name }))}
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ margin: 0, padding: 0 }}
                      />
                      <Bar dataKey="value" isAnimationActive={true} animationDuration={900}>
                        {filteredChartData.map((entry, index) => (
                          <Cell key={`bar-cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : chartDisplayType === 'polar' ? (
                    filteredChartData.length > 0 ? (
                      <RadialBarChart
                        data={polarData}
                        innerRadius="20%"
                        outerRadius="90%"
                        startAngle={90}
                        endAngle={-270}
                        barSize={30}
                      >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar
                          dataKey="value"
                          background
                          cornerRadius={10}
                          isAnimationActive={true}
                          animationDuration={900}
                          label={false}
                        >
                          {polarData.map((entry, index) => (
                            <Cell key={`polar-cell-${index}`} fill={entry.fill} />
                          ))}
                        </RadialBar>
                        <Tooltip
                          formatter={(val: number, name: string) =>
                            isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                          }
                        />
                        <Legend content={<PolarLegend />} />
                      </RadialBarChart>
                    ) : <div></div>
                  ) : <div>Unsupported chart type</div>}
                </ResponsiveContainer>
              ) : (
                <p>No data to display in chart.</p>
              )}
            </ChartWrapper>
          </NoOutlineWrapper>
          {tablePosition === 'right' && tableJSX}
        </SideBySideContainer>
      ) : (
        <React.Fragment>
          <NoOutlineWrapper>
            <ChartWrapper height={chartHeight}>
              {filteredChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  {chartDisplayType === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(val: number, name: string) => 
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        } 
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ margin: 0, padding: 0 }}
                      />
                    </PieChart>
                  ) : chartDisplayType === 'doughnut' ? (
                    <PieChart>
                      <Pie
                        data={filteredChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={chartHeight / 3}
                        innerRadius={chartHeight / 6}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={true}
                        animationDuration={900}
                        activeIndex={activeIndex}
                        activeShape={undefined}
                        onMouseLeave={() => setActiveIndex(-1)}
                      >
                        {filteredChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            onMouseEnter={() => setActiveIndex(index)}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(val: number, name: string) => 
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        } 
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ margin: 0, padding: 0 }}
                      />
                    </PieChart>
                  ) : chartDisplayType === 'bar' ? (
                    <BarChart data={filteredChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} width={chartHeight} height={chartHeight}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip 
                        formatter={(val: number, name: string) => 
                          isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                        } 
                      />
                      <Legend
                        payload={filteredChartData.map(item => ({ value: item.name, type: 'square', color: item.color, id: item.name }))}
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ margin: 0, padding: 0 }}
                      />
                      <Bar dataKey="value" isAnimationActive={true} animationDuration={900}>
                        {filteredChartData.map((entry, index) => (
                          <Cell key={`bar-cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  ) : chartDisplayType === 'polar' ? (
                    filteredChartData.length > 0 ? (
                      <RadialBarChart
                        data={polarData}
                        innerRadius="20%"
                        outerRadius="90%"
                        startAngle={90}
                        endAngle={-270}
                        barSize={30}
                      >
                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar
                          dataKey="value"
                          background
                          cornerRadius={10}
                          isAnimationActive={true}
                          animationDuration={900}
                          label={false}
                        >
                          {polarData.map((entry, index) => (
                            <Cell key={`polar-cell-${index}`} fill={entry.fill} />
                          ))}
                        </RadialBar>
                        <Tooltip
                          formatter={(val: number, name: string) =>
                            isPercentage ? [`${val.toFixed(1)}% (${name})`, 'Value'] : [`${val} (${name})`, 'Value']
                          }
                        />
                        <Legend content={<PolarLegend />} />
                      </RadialBarChart>
                    ) : <div></div>
                  ) : <div>Unsupported chart type</div>}
                </ResponsiveContainer>
              ) : (
                <p>No data to display in chart.</p>
              )}
            </ChartWrapper>
          </NoOutlineWrapper>
          {tablePosition === 'below' && tableJSX}
        </React.Fragment>
      )}
    </ChartContainer>
  )
} 